import useAppState from "@saleor/hooks/useAppState";
import useNotifier from "@saleor/hooks/useNotifier";
import { ApolloQueryResult } from "apollo-client";
import { useState } from "react";
import { useIntl } from "react-intl";

import { INITIAL_ROW_NUMBER } from "./constants";

interface IPaginationState {
  first?: number;
  last?: number;
  after?: string;
  before?: string;
}

interface IPageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string | null;
  endCursor: string | null;
}

const usePaginationState = <TRefetchVariable>(
  pageInfo: IPageInfo | undefined,
  initialRowNumber: number,
  refetch: (
    existingVariables: Partial<TRefetchVariable> & Partial<IPageInfo>
  ) => Promise<ApolloQueryResult<{}>>,
  refetchVariables: Partial<TRefetchVariable>
) => {
  const intl = useIntl();
  const notify = useNotifier();
  const [, dispatchAppState] = useAppState();
  const [rowNumber, setRowNumber] = useState(initialRowNumber);
  // stores the previous used pagination state, used for refetching needs.
  const [lastPaginationState, setLastPaginationState] = useState<
    IPaginationState
  >({ first: INITIAL_ROW_NUMBER });

  const handleCallRefetchApi = (
    variables: Partial<TRefetchVariable> & Partial<IPageInfo>
  ) => {
    dispatchAppState({
      payload: {
        value: true
      },
      type: "displayLoader"
    });
    refetch(variables)
      .catch(() => {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "An error occurred when fetching Unlock Codes"
          })
        });
      })
      .finally(() => {
        dispatchAppState({
          payload: {
            value: false
          },
          type: "displayLoader"
        });
      });
  };

  const onNextPage = () => {
    if (pageInfo) {
      const newPaginationState = {
        first: rowNumber,
        after: pageInfo.endCursor,
        last: undefined,
        before: undefined
      };
      handleCallRefetchApi({ ...refetchVariables, ...newPaginationState });
      setLastPaginationState(newPaginationState);
    }
  };

  const onPrevPage = () => {
    if (pageInfo) {
      const newPaginationState = {
        last: rowNumber,
        before: pageInfo.startCursor,
        first: undefined,
        after: undefined
      };
      handleCallRefetchApi({ ...refetchVariables, ...newPaginationState });
      setLastPaginationState(newPaginationState);
    }
  };

  const onRowNumberChange = (newRowNumber: number) => {
    setRowNumber(newRowNumber);
    const newPaginationState = {
      first: newRowNumber,
      after: undefined,
      last: undefined,
      before: undefined
    };
    handleCallRefetchApi({
      ...refetchVariables,
      ...newPaginationState
    });
    setLastPaginationState(newPaginationState);
  };

  const handleRefetchWithPaginatedState = () => {
    handleCallRefetchApi({
      ...refetchVariables,
      ...lastPaginationState
    });
  };

  return {
    handleRefetchWithPaginatedState,
    hasNextPage: pageInfo?.hasNextPage ?? false,
    hasPreviousPage: pageInfo?.hasPreviousPage ?? false,
    onNextPage,
    onPrevPage,
    onRowNumberChange,
    rowNumber
  };
};

export default usePaginationState;
